import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Key, FileText, Search, RefreshCw, X, PlusCircle, MinusCircle, Eye, EyeOff } from 'lucide-react';
import { adminApi } from '../../services/api';
import { AppUser, UserStatsResponse, SignupFormField } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const FIELD_TYPES: { value: SignupFormField['fieldType']; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'boolean', label: 'Yes/No' },
];

const emptyUserForm = () => ({ name: '', email: '', password: '', phone: '', isActive: true });
const emptyPwForm = () => ({ newPassword: '', showPassword: false });

const AppUsers: React.FC = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(false);

  // Create/edit user modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [userForm, setUserForm] = useState(emptyUserForm());

  // Reset password modal
  const [resetPwVisible, setResetPwVisible] = useState(false);
  const [resetPwUser, setResetPwUser] = useState<AppUser | null>(null);
  const [pwForm, setPwForm] = useState(emptyPwForm());

  // Signup form modal
  const [signupFormUser, setSignupFormUser] = useState<AppUser | null>(null);
  const [signupFormVisible, setSignupFormVisible] = useState(false);
  const [signupFormFields, setSignupFormFields] = useState<SignupFormField[]>([]);
  const [dropdownOptionsDraft, setDropdownOptionsDraft] = useState<string[]>([]);
  const [signupFormLoading, setSignupFormLoading] = useState(false);
  const [signupFormSaving, setSignupFormSaving] = useState(false);

  const [stats, setStats] = useState<UserStatsResponse | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({ role: '' as '' | 'admin' | 'client', isActive: '' as '' | 'true' | 'false', search: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAdmin) { fetchUsers(); fetchStats(); }
  }, [pagination.current, pagination.pageSize, filters, isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params: any = { page: pagination.current, limit: pagination.pageSize };
      if (filters.role) params.role = filters.role;
      if (filters.isActive !== '') params.isActive = filters.isActive === 'true';
      if (filters.search) params.search = filters.search;
      const response = await adminApi.getAllUsers(params);
      if (response.data.success && response.data.data) {
        setUsers(response.data.data.users);
        setPagination(p => ({ ...p, total: response.data.data!.pagination.total }));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      if (response.data.success && response.data.data) setStats(response.data.data);
    } catch {}
  };

  const openCreate = () => {
    setEditingUser(null);
    setUserForm(emptyUserForm());
    setModalVisible(true);
  };

  const openEdit = (user: AppUser) => {
    setEditingUser(user);
    setUserForm({ name: user.name, email: user.email, password: '', phone: user.phone || '', isActive: user.isActive });
    setModalVisible(true);
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userForm.name || !userForm.email) { toast.error('Name and email are required'); return; }
    if (!editingUser && !userForm.password) { toast.error('Password is required'); return; }
    try {
      if (editingUser) {
        const payload: any = { name: userForm.name, email: userForm.email, phone: userForm.phone, isActive: userForm.isActive };
        const response = await adminApi.updateUser(editingUser._id, payload);
        if (response.data.success) { toast.success('User updated successfully'); setModalVisible(false); fetchUsers(); }
      } else {
        const payload: any = { name: userForm.name, email: userForm.email, password: userForm.password, phone: userForm.phone };
        const response = await adminApi.createCompanyUser(payload);
        if (response.data.success) { toast.success('User created successfully'); setModalVisible(false); fetchUsers(); fetchStats(); }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await adminApi.deleteUser(id);
      if (response.data.success) { toast.success('User deleted successfully'); fetchUsers(); fetchStats(); }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (user: AppUser) => {
    try {
      const response = await adminApi.toggleUserStatus(user._id);
      if (response.data.success) {
        toast.success(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
        fetchUsers(); fetchStats();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to toggle user status');
    }
  };

  const openResetPw = (user: AppUser) => {
    setResetPwUser(user);
    setPwForm(emptyPwForm());
    setResetPwVisible(true);
  };

  const handleResetPwSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwForm.newPassword || pwForm.newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (!resetPwUser) return;
    try {
      const response = await adminApi.resetUserPassword(resetPwUser._id, { newPassword: pwForm.newPassword });
      if (response.data.success) { toast.success('Password reset successfully'); setResetPwVisible(false); }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const openSignupForm = async (user: AppUser) => {
    if (user.role !== 'client') return;
    setSignupFormUser(user);
    setSignupFormVisible(true);
    setSignupFormLoading(true);
    try {
      const response = await adminApi.getSignupForm(user._id);
      if (response.data.success && response.data.data?.signupFormFields) {
        const sorted = [...response.data.data.signupFormFields].sort((a, b) => a.order - b.order);
        setSignupFormFields(sorted);
        setDropdownOptionsDraft(sorted.map(f => (f.fieldType === 'dropdown' && Array.isArray(f.options) ? f.options.join(', ') : '')));
      } else {
        setSignupFormFields([]); setDropdownOptionsDraft([]);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load signup form');
      setSignupFormFields([]); setDropdownOptionsDraft([]);
    } finally {
      setSignupFormLoading(false);
    }
  };

  const handleSignupFormSave = async () => {
    if (!signupFormUser) return;
    const withOrder = signupFormFields.map((f, i) => {
      const base = { ...f, order: i };
      if (f.fieldType === 'dropdown') {
        const raw = dropdownOptionsDraft[i] ?? '';
        base.options = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : undefined;
      }
      return base;
    });
    if (!withOrder.every(f => f.fieldName?.trim() && f.fieldCode?.trim())) {
      toast.error('Each field must have a label and field code'); return;
    }
    setSignupFormSaving(true);
    try {
      const response = await adminApi.updateSignupForm(signupFormUser._id, { signupFormFields: withOrder });
      if (response.data.success) { toast.success('Signup form saved'); setSignupFormVisible(false); }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save signup form');
    } finally {
      setSignupFormSaving(false);
    }
  };

  const changeSignupField = (index: number, key: keyof SignupFormField, value: any) => {
    setSignupFormFields(prev => {
      const next = [...prev];
      (next[index] as any)[key] = value;
      if (key === 'fieldName' && typeof value === 'string') {
        next[index].fieldCode = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || next[index].fieldCode;
      }
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));

  if (!isAdmin) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500">You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">Client Management</h1>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats.totalUsers, color: 'text-gray-900' },
            { label: 'Active Users', value: stats.activeUsers, color: 'text-green-700' },
            { label: 'Admin Users', value: stats.adminUsers, color: 'text-red-700' },
            { label: 'Client Users', value: stats.companyUsers, color: 'text-blue-700' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={filters.search} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPagination(p => ({ ...p, current: 1 })); }} placeholder="Search by name or email" className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-60" />
          </div>
          <select value={filters.role} onChange={e => { setFilters(f => ({ ...f, role: e.target.value as any })); setPagination(p => ({ ...p, current: 1 })); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="client">Client</option>
          </select>
          <select value={filters.isActive} onChange={e => { setFilters(f => ({ ...f, isActive: e.target.value as any })); setPagination(p => ({ ...p, current: 1 })); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button onClick={() => { setFilters({ role: '', isActive: '', search: '' }); setPagination(p => ({ ...p, current: 1 })); fetchUsers(); fetchStats(); }} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-medium text-gray-700">Users</h2>
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Create Client User
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name', 'Email', 'Phone', 'Role', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No users found</td></tr>
              ) : users.map(user => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{user.name}</td>
                  <td className="px-4 py-3 text-gray-600">{user.email}</td>
                  <td className="px-4 py-3 text-gray-600">{user.phone || '–'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${user.role === 'admin' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                      {user.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${user.isActive ? 'text-green-700' : 'text-gray-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center flex-wrap gap-1">
                      <button onClick={() => openEdit(user)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"><Pencil className="w-3 h-3" /> Edit</button>
                      <button onClick={() => openResetPw(user)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"><Key className="w-3 h-3" /> Reset Pw</button>
                      {user.role === 'client' && (
                        <button onClick={() => openSignupForm(user)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"><FileText className="w-3 h-3" /> Form</button>
                      )}
                      <button onClick={() => handleToggleStatus(user)} className={`px-2 py-1 text-xs border rounded ${user.isActive ? 'border-orange-200 text-orange-700 hover:bg-orange-50' : 'border-green-200 text-green-700 hover:bg-green-50'}`}>
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => handleDelete(user._id)} disabled={user.role === 'admin'} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="w-3 h-3" /> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">Total: {pagination.total} users</p>
            <div className="flex items-center gap-2">
              <button disabled={pagination.current <= 1 || loading} onClick={() => setPagination(p => ({ ...p, current: p.current - 1 }))} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Prev</button>
              <span className="text-xs text-gray-600">Page {pagination.current} of {totalPages}</span>
              <button disabled={pagination.current >= totalPages || loading} onClick={() => setPagination(p => ({ ...p, current: p.current + 1 }))} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit User Modal */}
      {modalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">{editingUser ? 'Edit User' : 'Create Client User'}</h2>
              <button onClick={() => setModalVisible(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleUserSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input value={userForm.name} onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))} placeholder="Full Name" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input type="email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                  <input type="password" value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} placeholder="Password" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
                <input value={userForm.phone} onChange={e => setUserForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1234567890" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {editingUser && (
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <button type="button" onClick={() => setUserForm(f => ({ ...f, isActive: !f.isActive }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userForm.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userForm.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-sm text-gray-600">{userForm.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setModalVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">{editingUser ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPwVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Reset Password for {resetPwUser?.name}</h2>
              <button onClick={() => setResetPwVisible(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleResetPwSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type={pwForm.showPassword ? 'text' : 'password'} value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="New Password" className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button type="button" onClick={() => setPwForm(f => ({ ...f, showPassword: !f.showPassword }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {pwForm.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setResetPwVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Reset Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Signup Form Config Modal */}
      {signupFormVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Signup Form — {signupFormUser?.name || 'Client'}</h2>
              <button onClick={() => setSignupFormVisible(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-500 mb-4">These fields will appear on the event landing signup form for this client. Order is top to bottom.</p>
              {signupFormLoading ? (
                <div className="py-8 text-center text-gray-400">Loading...</div>
              ) : (
                <div className="space-y-3">
                  {signupFormFields.map((field, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <input placeholder="Label" value={field.fieldName} onChange={e => changeSignupField(index, 'fieldName', e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <input placeholder="Field code" value={field.fieldCode} onChange={e => changeSignupField(index, 'fieldCode', e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <select value={field.fieldType} onChange={e => changeSignupField(index, 'fieldType', e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                          <input type="checkbox" checked={field.isRequired} onChange={e => changeSignupField(index, 'isRequired', e.target.checked)} className="rounded" />
                          Req
                        </label>
                        <button type="button" onClick={() => {
                          setSignupFormFields(prev => prev.filter((_, i) => i !== index).map((f, i) => ({ ...f, order: i })));
                          setDropdownOptionsDraft(prev => prev.filter((_, i) => i !== index));
                        }} className="text-red-500 hover:text-red-700 p-1">
                          <MinusCircle className="w-4 h-4" />
                        </button>
                      </div>
                      {field.fieldType === 'dropdown' && (
                        <input placeholder="Options (comma-separated, e.g. Option A, Option B)" value={dropdownOptionsDraft[index] ?? ''} onChange={e => {
                          const next = [...dropdownOptionsDraft];
                          if (next.length <= index) next.length = index + 1;
                          next[index] = e.target.value;
                          setDropdownOptionsDraft(next);
                        }} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    setSignupFormFields(prev => [...prev, { fieldName: '', fieldCode: `field_${Date.now()}`, fieldType: 'text', isRequired: false, order: prev.length }]);
                    setDropdownOptionsDraft(prev => [...prev, '']);
                  }} className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600">
                    <PlusCircle className="w-4 h-4" /> Add field
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
              <button onClick={() => setSignupFormVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={handleSignupFormSave} disabled={signupFormSaving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                {signupFormSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppUsers;
